import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  List,
  ListItem,
  Text,
  Flex,
  Badge,
  useColorModeValue,
  Collapse,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useDebounce } from 'use-debounce';

const MotionBox = motion(Box);
const MotionListItem = motion(ListItem);

const SearchBar = ({ 
  products = [], 
  onSearchResults, 
  placeholder = "Search products...",
  maxSuggestions = 6,
  loading = false,
  resetSearchRef
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [isTyping, setIsTyping] = useState(false); // Track if user is actively typing
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionRefs = useRef([]);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      onSearchResults(products); // Show all products when search is empty
      return;
    }

    // Filter products based on search query
    const filteredSuggestions = products
      .filter(product => {
        const name = product.name || '';
        const category = product.category || product.productCategory || '';
        const searchTerm = debouncedSearchQuery.toLowerCase();
        return name.toLowerCase().includes(searchTerm) ||
               category.toLowerCase().includes(searchTerm);
      })
      .slice(0, maxSuggestions);

    setSuggestions(filteredSuggestions);
    // Only show suggestions if user is actively typing and input has focus
    setShowSuggestions(filteredSuggestions.length > 0 && isTyping && document.activeElement === inputRef.current);
    setSelectedIndex(-1);

    // Filter products for search results
    const searchResults = products.filter(product => {
      const name = product.name || '';
      const category = product.category || product.productCategory || '';
      const searchTerm = debouncedSearchQuery.toLowerCase();
      return name.toLowerCase().includes(searchTerm) ||
             category.toLowerCase().includes(searchTerm);
    });

    onSearchResults(searchResults);
  }, [debouncedSearchQuery, products, maxSuggestions, onSearchResults, isTyping]);

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setIsTyping(true); // User is actively typing
  };

  const handleInputFocus = () => {
    // Don't automatically show suggestions on focus
    // They will only show when user types (handled by useEffect)
  };

  const handleInputBlur = () => {
    // Close suggestions when input loses focus and stop typing state
    // Small delay to allow for suggestion clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
      setIsTyping(false);
    }, 200);
  };

  const resetSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setIsTyping(false);
    onSearchResults(products);
  };

  // Expose reset function to parent component
  useEffect(() => {
    if (resetSearchRef) {
      resetSearchRef.current = resetSearch;
    }
  }, [resetSearchRef, products, onSearchResults]);

  const handleSuggestionClick = (product) => {
    // First update the search query
    setSearchQuery(product.name);
    
    // Filter and set results
    const searchResults = products.filter(p => 
      p.name.toLowerCase().includes(product.name.toLowerCase())
    );
    onSearchResults(searchResults);
    
    // Then close dropdown and stop typing state
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setIsTyping(false); // User is no longer typing, they selected
    
    // Remove focus to prevent dropdown from reopening
    inputRef.current?.blur();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setIsTyping(false);
    onSearchResults(products);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          // If no suggestion selected, just close dropdown
          setShowSuggestions(false);
          setIsTyping(false);
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        setIsTyping(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
        setIsTyping(false);
      }
    };

    // Only add event listeners when suggestions are visible
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  const formatPrice = (price) => {
    if (!price) return 'Price not available';
    // Handle both string and number prices from API
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'Price not available';
    return `₹${numericPrice.toLocaleString('en-IN')}`;
  };


  return (
    <Box 
      position="relative" 
      w="100%" 
      maxW="800px" 
      mx="auto" 
      ref={searchRef}
      zIndex="1" // Create stacking context
      pb={{ base: 4, md: 6, lg: 8 }} // Responsive bottom padding: mobile/tablet/desktop
    >
      {/* Modern Search Container */}
      <Box
        position="relative"
        bg="linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.95) 100%)"
        backdropFilter="blur(20px)"
        border="1px solid"
        borderColor="rgba(26, 25, 25, 0.3)"
        borderRadius="2xl"
        boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
        _focusWithin={{
          transform: "translateY(-3px)",
          boxShadow: "0 35px 60px -12px rgba(99, 102, 241, 0.3)",
          borderColor: "rgba(99, 102, 241, 0.6)",
          bg: "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(249,250,251,1) 100%)",
        }}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        overflow="hidden"
      >
        {/* Search Input */}
        <Flex align="center" position="relative">
          {/* Left Icon */}
          <Box
            position="absolute"
            left={{ base: 4, md: 6 }}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            pointerEvents="none"
          >
            {loading ? (
              <Spinner size={{ base: "sm", md: "md" }} color="gray.500" thickness="3px" />
            ) : (
              <SearchIcon 
                color="gray.500" 
                boxSize={{ base: 4, md: 6 }}
              />
            )}
          </Box>

          {/* Main Input */}
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            bg="transparent"
            border="none"
            borderRadius="0"
            pl={{ base: 12, md: 16 }}
            pr={searchQuery ? { base: 12, md: 16 } : { base: 6, md: 8 }}
            py={{ base: 5, md: 8 }}
            fontSize={{ base: 'md', sm: 'lg', md: 'xl' }}
            fontWeight="medium"
            color="gray.800"
            _placeholder={{
              color: 'gray.400',
              fontSize: { base: 'md', sm: 'lg', md: 'xl' },
              fontWeight: 'normal'
            }}
            _focus={{
              outline: 'none',
              boxShadow: 'none',
            }}
            _hover={{
              bg: 'transparent'
            }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />

          {/* Right Clear Button */}
          {searchQuery && (
            <Box
              position="absolute"
              right={4}
              zIndex={2}
            >
              <IconButton
                aria-label="Clear search"
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                borderRadius="full"
                onClick={handleClearSearch}
                _hover={{ 
                  bg: 'gray.100',
                  transform: 'scale(1.1)'
                }}
                transition="all 0.2s ease"
              />
            </Box>
          )}
        </Flex>

        {/* Animated Search Bar Bottom Accent */}
        <Box
          position="absolute"
          bottom={0}
          left="50%"
          transform="translateX(-50%)"
          w={searchQuery || showSuggestions ? "100%" : "0%"}
          h="3px"
          bg="linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>

      {/* Dropdown rendered with absolute positioning relative to search container */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <MotionBox
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            position="absolute"
            top="calc(100% + 8px)"
            left={0}
            right={0}
            bg="linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)"
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor="rgba(255,255,255,0.3)"
            borderRadius="2xl"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.35)"
            zIndex={2147483647} // Maximum z-index value
            maxH="400px"
            overflowY="auto"
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                bg: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                bg: 'rgba(0,0,0,0.1)',
                borderRadius: 'full',
              },
            }}
          >
            <List spacing={0} p={2}>
              {suggestions.map((product, index) => (
                <MotionListItem
                  key={product.id}
                  ref={el => suggestionRefs.current[index] = el}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  p={4}
                  cursor="pointer"
                  bg={selectedIndex === index ? 'rgba(99, 102, 241, 0.1)' : 'transparent'}
                  borderRadius="xl"
                  _hover={{ 
                    bg: 'rgba(99, 102, 241, 0.05)',
                    transform: 'translateY(-1px)'
                  }}
                  style={{
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleSuggestionClick(product)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                  mb={index < suggestions.length - 1 ? 1 : 0}
                >
                  <Flex align="center" justify="space-between" w="100%">
                    <Text 
                      fontWeight="600" 
                      fontSize={{ base: 'md', md: 'lg' }}
                      color="gray.800"
                      noOfLines={1}
                      flex={1}
                      mr={4}
                    >
                      {product.name}
                    </Text>
                    <Text 
                      fontWeight="600" 
                      color="gray.700"
                      fontSize={{ base: 'md', md: 'lg' }}
                      flexShrink={0}
                    >
                      {formatPrice(product.price)}
                    </Text>
                  </Flex>
                </MotionListItem>
              ))}
            </List>
          </MotionBox>
        )}

        {showSuggestions && searchQuery && suggestions.length === 0 && !loading && (
          <MotionBox
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            position="absolute"
            top="calc(100% + 8px)"
            left={0}
            right={0}
            bg="linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)"
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor="rgba(255,255,255,0.3)"
            borderRadius="2xl"
            p={8}
            textAlign="center"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
            zIndex={2147483647}
          >
            <VStack spacing={4}>
              <Box
                p={4}
                bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                borderRadius="full"
                display="inline-flex"
              >
                <SearchIcon color="white" boxSize={6} />
              </Box>
              <VStack spacing={2}>
                <Text fontWeight="600" color="gray.800" fontSize="lg">
                  No products found
                </Text>
                <Text fontSize="md" color="gray.600">
                  Try searching with different keywords or check spelling
                </Text>
              </VStack>
            </VStack>
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default SearchBar;